import type { Module } from '@/types/course';

export const module3: Module = {
  slug: 'modulo-3-comprar-oro-seguro',
  number: 3,
  title: 'Cómo comprar oro de forma segura',
  description:
    'Saber qué oro quieres no es suficiente: hay que saber dónde comprarlo, a qué precio, con qué garantías y respetando la fiscalidad. La guía práctica más importante del curso.',
  durationMinutes: 80,
  badge: 'comprador-astuto',
  lessons: [
    {
      slug: 'distribuidores-certificados',
      number: '3.1',
      title: 'Distribuidores certificados y cómo identificarlos',
      durationMinutes: 18,
      videoUrl: null,
      content: `El mercado del oro está poblado de actores de muy distinta calidad. Saber distinguirlos es la primera línea de defensa del inversor.

**Categorías de distribuidores**:

**1. Refinerías LBMA Good Delivery (mayoristas)**: Argor-Heraeus, PAMP, Valcambi, Umicore, Heraeus, Royal Canadian Mint. No venden directamente al particular en cantidades pequeñas, pero su sello es la máxima garantía.

**2. Distribuidores profesionales especializados (minoristas premium)**: empresas con licencias específicas, ubicación física, asociaciones profesionales, auditorías. **Oro Express** es ejemplo de este modelo. Lo característico:
- Domicilio físico verificable.
- Inventario físico mostrable.
- Trabajan con refinerías LBMA.
- Contabilidad transparente y cumplen normativa AML.
- Ofrecen factura legal con desglose (IVA 0% en oro de inversión).
- Permiten verificación de autenticidad.

**3. Bancos y entidades financieras**: algunos venden monedas bullion y lingotes pequeños. Garantías altas, pero **primas muy elevadas** (8-15% sobre spot), gama limitada, poca flexibilidad.

**4. Compraventas locales y casas de empeño**: pueden ser legítimas, pero su negocio principal es comprar oro usado del público, no vender oro de inversión nuevo.

**5. Plataformas online sin presencia física**: hay opciones legítimas y muchas dudosas. **Cuidado** con webs nuevas, sin dirección física, con precios sospechosamente bajos, o que solo aceptan transferencias a cuentas extranjeras.

**6. Particulares**: legalmente puedes comprar a otro particular. Riesgos: falta de garantía de autenticidad, sin factura, posibles problemas fiscales y, en casos extremos, riesgo de adquirir oro de origen ilícito.

**Checklist de verificación**:
1. ✅ Domicilio fiscal y físico verificable.
2. ✅ CIF accesible (Registro Mercantil).
3. ✅ Antigüedad mínima (5+ años recomendable).
4. ✅ Asociación profesional del sector.
5. ✅ Cumplimiento AML: te pedirán DNI para compras superiores a 1.000€. **Si no te lo piden, es señal de alarma**.
6. ✅ Factura completa con desglose, número de serie, datos del distribuidor y comprador.
7. ✅ Política de recompra clara.
8. ✅ Reseñas verificables.

**Banderas rojas**:
- ❌ Precios muy por debajo de la media (5%+ de descuento sobre el spot).
- ❌ No piden documentación.
- ❌ Solo aceptan efectivo o cripto sin alternativa bancaria.
- ❌ Web sin domicilio físico, sin teléfono fijo, sin nombres de personas responsables.
- ❌ Presión de venta urgente.
- ❌ Promesas de revalorización extraordinaria.
- ❌ Oro a precios extraordinarios sin certificación clara.

> *Este contenido es educativo y no constituye asesoramiento financiero personalizado.*`,
      keyPoints: [
        'Las refinerías LBMA Good Delivery son el estándar mundial; el oro de inversión legítimo viene de ellas.',
        'Distribuidores profesionales especializados ofrecen el mejor equilibrio precio/garantías.',
        'Bancos venden seguro pero caro; compraventas locales y particulares tienen riesgos reales.',
        'Verifica siempre: domicilio, antigüedad, AML, factura completa y política de recompra.',
        'Banderas rojas: precios anormalmente bajos, ausencia de documentación, presión de venta, promesas extraordinarias.',
      ],
    },
    {
      slug: 'spot-prima-margenes',
      number: '3.2',
      title: 'Spot price, prima y márgenes',
      durationMinutes: 22,
      videoUrl: null,
      content: `Cuando ves "el oro está a 2.450 €/oz", ese es el **spot price**, no el precio que pagarás. Entender la diferencia entre spot, prima y precio final es lo que separa al comprador informado del impulsivo.

**El spot price**. Es el precio internacional al que se intercambia oro en el mercado mayorista para entrega inmediata. Se fija continuamente en el LBMA (Londres) y COMEX (Nueva York). Cotiza tradicionalmente en **dólares por onza troy** (1 onza troy = 31,1035 gramos). Para convertir a euros por gramo:

\`\`\`
Precio €/g = (Precio $/oz × tipo cambio EUR/USD) ÷ 31,1035
\`\`\`

Ejemplo: oro a 2.450 USD/oz, EUR/USD = 1,07. Precio en euros por gramo:

\`\`\`
(2.450 × (1/1,07)) ÷ 31,1035 = 73,60 €/g aproximadamente
\`\`\`

El spot fluctúa cada segundo. Distribuidores profesionales actualizan precios cada 15-60 minutos.

**La prima**. Sobreprecio que el distribuidor cobra sobre el spot. Cubre:
1. Refino y acuñación.
2. Logística y transporte seguro.
3. Seguro de tránsito y almacenaje.
4. Costes operativos.
5. Margen comercial.
6. Prima de mercado (en momentos de alta demanda física, sube).

**Primas típicas** (orientativas):

| Producto | Prima típica sobre spot |
|---|---|
| Lingote 1 g | 8-15% |
| Lingote 10 g | 4-7% |
| Lingote 50 g | 3-5% |
| Lingote 100 g | 2-3,5% |
| Lingote 250 g | 1,5-2,5% |
| Lingote 500 g | 1,2-2% |
| Lingote 1 kg | 0,8-1,8% |
| Krugerrand 1 oz | 4-6% |
| Maple Leaf 1 oz | 4-6% |
| Filarmónica 1 oz | 4-6% |
| Britannia 1 oz | 4-7% |
| Bullion fraccionada | 7-12% |

**El precio de recompra (spread del distribuidor)**. Cuando vendes oro a un distribuidor, no te paga el spot: te paga el spot menos un descuento.

- Lingotes 100g+: 0,5-2% por debajo del spot
- Lingotes pequeños y monedas: 1-5% por debajo del spot

El **spread total** (diferencia entre prima de compra y descuento de venta) es lo que realmente "te cuesta" tener el oro. Para un lingote de 100g, el spread total puede ser del 3-5%; para una moneda de 1/10 oz, puede llegar al 15%.

**Implicación práctica**: si compras y vendes inmediatamente, pierdes el spread completo. **El oro físico está pensado para horizontes de años**, no días o meses.

**Cómo evaluar si una prima es justa**:
1. Consulta el spot del día (kitco.com, goldprice.org).
2. Calcula la prima del distribuidor: \`(Precio venta − Spot) ÷ Spot × 100\`.
3. Compárala con la prima típica del producto.
4. Si está dentro del rango ±20% de la prima típica, es razonable.

**El timing de la compra**. ¿Importa el momento? Sí, pero menos de lo que la gente cree. A horizontes largos (10+ años), comprar en buen momento o malo dentro del mismo año cambia poco. Lo que sí importa:
- **No comprar en pánico** durante picos de demanda (las primas se disparan).
- **Aprovechar correcciones técnicas** (caídas del 5-10% sobre máximos recientes).
- **Promediar la entrada** (DCA) si tienes mucho capital.

> *Este contenido es educativo y no constituye asesoramiento financiero personalizado.*`,
      keyPoints: [
        'Spot price = precio mayorista internacional; precio final = spot + prima.',
        'Las primas son menores cuanto mayor el lingote: 1g (10%) vs 1kg (1,5%).',
        'El spread total (compra-venta) puede ser 3-15% según formato.',
        'El oro físico requiere horizontes de años para amortizar el spread inicial.',
        'Comprar bien implica conocer el spot y comparar primas; promediar entrada (DCA) reduce riesgo de timing.',
      ],
    },
    {
      slug: 'fiscalidad-oro-espana',
      number: '3.3',
      title: 'Fiscalidad del oro de inversión en España',
      durationMinutes: 20,
      videoUrl: null,
      content: `La fiscalidad del oro en España es **inusualmente favorable** comparada con casi cualquier otra inversión.

**1. IVA: EXENCIÓN TOTAL para oro de inversión**

La normativa española —Directiva europea 98/80/CE— establece que el **oro de inversión está exento de IVA**. Incluye:
- Lingotes con pureza ≥ 99,5%, en formatos aceptados por el mercado.
- Monedas con pureza ≥ 90%, acuñadas posteriormente a 1800, que sean o hayan sido moneda de curso legal en su país, y cuyo precio no supere el 80% el valor del oro contenido.

Las cuatro grandes monedas bullion están explícitamente incluidas en la lista oficial publicada anualmente por la AEAT.

**Implicación**: cuando compras un lingote de 1 kg a 73.000€, pagas 73.000€ (más prima), sin IVA del 21%. Si fuera gravado, pagarías 88.330€. **El oro es uno de los pocos activos sin IVA en compra**.

**Cuidado**: el oro **no de inversión** (joyería, oro industrial, oro coleccionable que no cumpla los requisitos) **sí lleva IVA del 21%**. Comprar una pulsera como "inversión" es un error fiscal.

**2. Impuesto sobre Patrimonio**

El oro forma parte del patrimonio y se declara si superas el mínimo exento autonómico. En Madrid existe bonificación cercana al 100%; en Cataluña el mínimo está en 500.000€, con tipos progresivos hasta el 3,48%. Se declara por su **valor de mercado al 31 de diciembre**.

**3. IRPF — tributación de la ganancia patrimonial**

Cuando vendes oro y obtienes ganancia, tributa en la base imponible del ahorro:

- 19% para los primeros 6.000€
- 21% entre 6.000€ y 50.000€
- 23% entre 50.000€ y 200.000€
- 27% entre 200.000€ y 300.000€
- 28% por encima de 300.000€

**Cálculo**: precio de venta − precio de compra original − gastos asociados (factura, comisiones documentadas).

**Importante**: Conserva **siempre las facturas de compra**. Sin factura, Hacienda puede asumir precio cero, lo que dispararía la tributación.

**4. Compensación de pérdidas**

Las pérdidas pueden **compensarse con ganancias patrimoniales** del mismo ejercicio. El saldo negativo se puede arrastrar a 4 ejercicios siguientes.

**5. Oro heredado o donado**

Cuando heredas oro, tributas por **Impuesto sobre Sucesiones y Donaciones**, con valoración a precio de mercado en el fallecimiento. El tipo varía mucho por Comunidad Autónoma. Si después vendes, la **ganancia patrimonial** se calcula desde el valor declarado en la sucesión.

**6. Obligación de declarar**

- **Compra**: NO obligación de declarar específicamente.
- **Venta con ganancia**: SÍ obligación de declarar en el IRPF.
- **Patrimonio elevado**: SÍ declarar en Patrimonio si supera mínimo autonómico.
- **Custodia en el extranjero**: SÍ Modelo 720 si supera 50.000€ fuera de España.

**7. Operaciones en efectivo**

La normativa antiblanqueo limita pagos en efectivo:
- Pagos entre empresarios y un particular: **máximo 1.000€** (2.500€ si pagador es no residente).
- Pagos superiores deben hacerse por medios bancarios trazables.

> **Disclaimer importante**: La fiscalidad cambia con frecuencia y depende de circunstancias personales. Este contenido es educativo a fecha del curso. Consulta siempre con un asesor fiscal cualificado.`,
      keyPoints: [
        'El oro de inversión está exento de IVA en España (lingotes ≥99,5% y monedas bullion oficiales).',
        'Las ganancias por venta tributan en IRPF (base ahorro): 19-28% según importe.',
        'Conservar facturas de compra es esencial.',
        'En Patrimonio se declara a valor de mercado a 31/12; varía mucho por CCAA.',
        'Pagos en efectivo limitados a 1.000€; obligatorio uso de medios bancarios para mayores.',
      ],
    },
    {
      slug: 'errores-comprador-novato',
      number: '3.4',
      title: 'Errores frecuentes del comprador novato',
      durationMinutes: 20,
      videoUrl: null,
      content: `Los errores más caros casi nunca son sofisticados: son básicos y previsibles.

**Error 1: Comprar joyería como inversión**. La joyería lleva IVA del 21%, mano de obra (100-300% del valor metálico), y se vende como oro usado al peso. Una alianza de 800€ se compra por 200-300€. **Pérdida garantizada del 60-70%**.

**Error 2: Pagar primas excesivas por monedas pequeñas**. Diez monedas de 1/10 oz cuestan más que una de 1 oz por la misma cantidad de oro. Salvo necesidad real de liquidez fragmentada, **prioriza tamaños medios o grandes**.

**Error 3: Comprar a un distribuidor sin verificar**. Si no puedes verificar domicilio físico, antigüedad, registro mercantil, asociaciones profesionales y reseñas, no compres.

**Error 4: No conservar las facturas**. Sin factura, no puedes demostrar precio de adquisición ante Hacienda. **Cada factura es un documento patrimonial**.

**Error 5: Romper el blister/sello del lingote**. Un lingote en blister sellado se vende casi a precio "como nuevo". Fuera del blister levanta sospechas y reduce el precio entre 1% y 5%. **Nunca rompas el blister**.

**Error 6: Custodia descuidada**. Guardar oro en un cajón es un error. Pero también lo es publicitar la caja fuerte. **El primer principio de la custodia es la discreción**.

**Error 7: Concentrarse en un solo formato**. Todo en lingotes de 1 kg es eficiente en coste pero rígido en liquidez. Todo en monedas es flexible pero costoso. **Diversificar tamaños es más eficiente**.

**Error 8: Vender en pánico**. El oro tiene correcciones del 5-15% varias veces al año. Quien vende en la primera caída pierde dinero sistemáticamente. **El oro físico es horizonte largo**.

**Error 9: Ignorar el seguro**. Las pólizas de hogar tienen coberturas limitadas para metales preciosos (típicamente 3.000-6.000€). Para patrimonios mayores, necesitas póliza específica.

**Error 10: Comprar bajo presión o promesas extraordinarias**. "Solo hoy", "última oportunidad", "rentabilidad garantizada del 30%". Cualquier distribuidor profesional jamás presiona.

**Error 11: Olvidar que el oro es ilíquido para emergencias inmediatas**. El oro es activo refugio, no fondo de emergencia. Vender lleva 24-72h. **Mantén un fondo de emergencia separado**.`,
      keyPoints: [
        'Joyería ≠ inversión: pérdidas garantizadas del 60-70% en reventa.',
        'Diversificar tamaños equilibra coste y flexibilidad.',
        'Conservar facturas y blisters originales preserva valor patrimonial.',
        'Custodia con discreción + seguro adecuado son críticos.',
        'El oro es horizonte largo: vender en pánico ante correcciones es el error más caro.',
      ],
    },
  ],
  quiz: [
    {
      id: 'm3-q1',
      question: 'En España, ¿el oro de inversión está sujeto a IVA?',
      options: [
        'Sí, al 21%',
        'Sí, al 10%',
        'No, está exento de IVA si cumple los requisitos',
        'Solo los lingotes, las monedas sí pagan',
      ],
      correctIndex: 2,
      explanation:
        'La Directiva 98/80/CE y la Ley del IVA española eximen al oro de inversión que cumpla los requisitos de pureza y formato.',
    },
    {
      id: 'm3-q2',
      question: '¿Qué documento debes conservar OBLIGATORIAMENTE tras comprar oro?',
      options: [
        'Una foto del lingote',
        'La factura original con número de serie',
        'Un informe del distribuidor',
        'Solo la transferencia bancaria',
      ],
      correctIndex: 1,
      explanation:
        'Sin factura, Hacienda puede asumir precio de adquisición cero al vender, disparando la tributación. La factura es documento patrimonial.',
    },
    {
      id: 'm3-q3',
      question:
        '¿Cuál es el límite legal en España para pagos en efectivo entre un empresario y un particular?',
      options: ['500€', '1.000€', '3.000€', 'No hay límite'],
      correctIndex: 1,
      explanation:
        'La Ley antiblanqueo establece 1.000€ como máximo en efectivo (2.500€ si el pagador es no residente fiscal).',
    },
    {
      id: 'm3-q4',
      question: '¿Cuál es una BANDERA ROJA al evaluar un distribuidor?',
      options: [
        'Tiene domicilio físico verificable',
        'Pide DNI para compras superiores a 1.000€',
        'Acepta solo efectivo o cripto y no pide documentación',
        'Lleva más de 10 años operando',
      ],
      correctIndex: 2,
      explanation:
        'La ausencia de cumplimiento AML (no pedir documentación) y la insistencia en métodos de pago no trazables son señales claras de actividad sospechosa.',
    },
    {
      id: 'm3-q5',
      question:
        'Si tienes una alianza de oro de 18 quilates comprada por 1.200€, ¿cuánto te pagarían aproximadamente al venderla a un distribuidor profesional?',
      options: [
        '1.200€ (mismo precio)',
        '1.000€',
        'Entre 300€ y 500€ aproximadamente',
        '1.500€ por la revalorización',
      ],
      correctIndex: 2,
      explanation:
        'La joyería se compra al peso del oro contenido, descontando merma. Una alianza de 18k de 4-5 g a precio actual ronda los 300-400€. La pérdida del 60-70% es típica al revender joyería.',
    },
  ],
};
